import { STANDARDISED_AMENITIES_MAP } from 'constants/amenities'

/**
 * Reorders amenities/popular facilities by boosting those that map to an active amenity filter.
 * Uses STANDARDISED_AMENITIES_MAP to translate facility description to standardized category.
 * Non-matching amenities maintain their original relative order.
 *
 * @param amenities - Array of OfferInclusions to reorder (has description field from popularFacilities)
 * @param activeAmenities - Set of amenity category strings to boost (e.g., Set(['Private pool', 'Coffee machine']))
 * @returns New array with matching amenities boosted to the top
 */
export function reorderAmenitiesByActiveFilters(
  amenities: Array<string>,
  activeAmenities: Set<string>,
): Array<string> {
  if (!activeAmenities.size || !amenities.length) {
    return amenities
  }

  const boosted: Array<string> = []
  const rest: Array<string> = []
  for (const amenity of amenities) {
    // Look up the facility description in STANDARDISED_AMENITIES_MAP to get standardized category
    // Note: popularFacilities is Array<string>, and many strings won't be in the map (returns undefined)
    const amenityCategory = amenity ? STANDARDISED_AMENITIES_MAP[amenity] : undefined
    if (amenityCategory && activeAmenities.has(amenityCategory)) {
      boosted.push(amenity)
    } else {
      rest.push(amenity)
    }
  }

  return [...boosted, ...rest]
}

export function reorderDescribedAmenitiesByActiveFilters<T extends { description?: string }>(
  amenities: Array<T>,
  activeAmenities: Set<string>,
): Array<T> {
  if (!activeAmenities.size || !amenities.length) {
    return amenities
  }

  const boosted: Array<T> = []
  const rest: Array<T> = []
  for (const amenity of amenities) {
    const amenityCategory = amenity.description ? STANDARDISED_AMENITIES_MAP[amenity.description] : undefined
    if (amenityCategory && activeAmenities.has(amenityCategory)) {
      boosted.push(amenity)
    } else {
      rest.push(amenity)
    }
  }

  return [...boosted, ...rest]
}
